/**
 * useWallSession — wall cart + decoupled uploads.
 *
 * Uploads go to /api/wall/upload (lean: hosts the original on S3/CloudFront,
 * Cloudinary fetch-transforms it for the smart crop) IN PARALLEL, and do NOT
 * run getImagesDB per image — the wall renders from its own local state. The
 * app's cart atoms are hydrated ONCE, at checkout, via hydrateForCheckout().
 *
 * Session: the wall uses the app's ONE shared session (uniqueUserId/sessionId) —
 * the user's single gallery — so no per-wall keys and nothing clobbers it across
 * tabs. Checkout is gated to exactly N placed frames; extras are never auto-deleted.
 */
import { useCallback, useRef } from "react";
import axios from "axios";
import { getImagesDB } from "@api";
import { randId, DEFAULT_FRAME } from "./data";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const LS = (k, v) => {
  try {
    if (v == null) return localStorage.getItem(k);
    localStorage.setItem(k, v);
    return v;
  } catch (e) {
    return null;
  }
};

// sessionStorage survives the app's localStorage.clear() on payment success. We mirror
// the gallery session here so a wall order that keeps un-ordered images can re-find the
// (still-active) cart afterwards and repopulate the gallery.
const SS = (k, v) => {
  try {
    if (v == null) return sessionStorage.getItem(k);
    sessionStorage.setItem(k, v);
    return v;
  } catch (e) {
    return null;
  }
};

const stashSession = (uid, sid) => {
  if (uid) SS("wallUid", uid);
  if (sid) SS("wallSid", sid);
};

export default function useWallSession() {
  const wall = useRef({ sid: null, uid: null });
  const starting = useRef(null);

  const ensureSession = useCallback(() => {
    if (wall.current.uid && wall.current.sid) return Promise.resolve(wall.current);
    if (starting.current) return starting.current;

    // ONE shared session per visitor = the app's uniqueUserId/sessionId (the user's
    // single gallery). Every wall + the main flow read/write it, so nothing clobbers
    // it across tabs and getImagesDB reads it natively. Mint once only if absent.
    let uid = LS("uniqueUserId");
    if (!uid) { uid = "w" + randId(11); LS("uniqueUserId", uid); }
    wall.current.uid = uid;
    if (!LS("frameSelected")) LS("frameSelected", DEFAULT_FRAME);

    const existingSid = LS("sessionId");
    if (existingSid) {
      wall.current.sid = existingSid;
      stashSession(wall.current.uid, wall.current.sid);
      starting.current = Promise.resolve(wall.current);
      return starting.current;
    }

    starting.current = axios
      .get(`${BASE_URL}/generate-session-id`)
      .then((r) => { wall.current.sid = (r.data && r.data.sessionId) || "b" + randId(5); })
      .catch(() => { wall.current.sid = "b" + randId(5); })
      .then(() => {
        LS("sessionId", wall.current.sid);
        stashSession(wall.current.uid, wall.current.sid);
        return axios
          .post(`${BASE_URL}/addtocart`, {
            uid: wall.current.uid,
            sessionId: wall.current.sid,
            userInfo: {},
            frameType: LS("frameSelected") || DEFAULT_FRAME,
            source: "wall",
          })
          .catch(() => {});
      })
      .then(() => wall.current);

    return starting.current;
  }, []);

  /**
   * Upload one image via the lean wall endpoint. Returns the created record
   * (with _id, publicId, image, crop_image, view_image). Does NOT run getImagesDB.
   */
  const uploadImage = useCallback(
    (file, w, h, bw, region, net) => {
      const uniqueId = "w" + Date.now() + Math.floor(Math.random() * 1e6);
      return ensureSession().then(() => {
        const fd = new FormData();
        fd.append("image", file);
        fd.append("uid", wall.current.uid);
        fd.append("sessionId", wall.current.sid);
        fd.append("uniqueId", uniqueId);
        fd.append("frametype", LS("frameSelected") || DEFAULT_FRAME);
        fd.append("imagewidth", w || 0);
        fd.append("imageheight", h || 0);
        fd.append("uploadedAtTime", Date.now());
        fd.append("bw", bw ? "true" : "false");
        // Client-decided smart crop -> server uses c_crop (not g_auto) so the
        // print matches the instant preview the user already saw. File may already
        // be short-edge-capped client-side (UPLOAD_MAX_SHORT_EDGE) before this call.
        if (region && region.size) {
          fd.append("cropX", region.x);
          fd.append("cropY", region.y);
          fd.append("cropSize", region.size);
        }
        return axios
          .post(`${(net && net.baseUrl) || BASE_URL}/wall/upload`, fd, {
            headers: { "content-type": "multipart/form-data" },
            // Resilient-transport hooks (uploadResilience): abort signal for the
            // stall watchdog + progress events that feed it; baseUrl lets retries
            // alternate the CloudFront-accelerated and direct endpoints.
            signal: net && net.signal,
            onUploadProgress: net && net.onUploadProgress,
          })
          .then((r) => r.data && r.data.image);
      });
    },
    [ensureSession]
  );

  // Re-crop an already-uploaded image. `region` (manual square, original px) may
  // be null for a bw-only toggle — the server preserves the existing crop.
  const updateCrop = useCallback((imageId, region, bw) => {
    return axios
      .post(`${BASE_URL}/wall/crop`, {
        uid: wall.current.uid,
        sessionId: wall.current.sid,
        imageId,
        bw: bw ? "true" : "false",
        ...(region ? { cropX: region.x, cropY: region.y, cropSize: region.size } : {}),
      })
      .then((r) => r.data);
  }, []);

  // Hydrate the app cart atoms (imageCount/netPrice/imagesData...) for checkout from
  // the ONE shared session — getImagesDB reads uniqueUserId/sessionId natively.
  const hydrateForCheckout = useCallback(() => getImagesDB().catch(() => null), []);

  // Resume the user's ONE gallery. A wall order keeps the un-ordered images in an
  // ACTIVE (non-purchaseCompleted) cart, but the app's localStorage.clear() on payment
  // success wipes the pointer — so fall back to the sessionStorage stash and rehydrate
  // localStorage from it. A cart that IS purchaseCompleted (a normal full order) is
  // forgotten so the gallery starts fresh. Ordered images are already excluded by the
  // server (imageuser filters orderedInOid), so what loads is exactly the leftovers.
  const load = useCallback((uid, sid) => {
    wall.current.uid = uid;
    wall.current.sid = sid;
    stashSession(uid, sid);
    starting.current = Promise.resolve(wall.current);
    return getImagesDB().catch(() => null);
  }, []);

  const restore = useCallback(() => {
    let uid = LS("uniqueUserId") || SS("wallUid");
    const sid = LS("sessionId") || SS("wallSid");
    if (!sid) return Promise.resolve(null);
    // Rehydrate localStorage from the stash (getUserImages/getImagesDB read it natively).
    if (uid && !LS("uniqueUserId")) LS("uniqueUserId", uid);
    if (!LS("sessionId")) LS("sessionId", sid);
    return axios
      .get(`${BASE_URL}/addtocart/session/${sid}`)
      .then((r) => {
        const cart = r && r.data && r.data.cart;
        if (cart && cart.purchaseCompleted) {
          try {
            localStorage.removeItem("uniqueUserId");
            localStorage.removeItem("sessionId");
            sessionStorage.removeItem("wallUid");
            sessionStorage.removeItem("wallSid");
          } catch (e) { /* ignore */ }
          return null; // fully purchased -> gallery starts fresh
        }
        // Cross-device: a shared ?s=<sessionId> URL seeds only the sessionId; adopt the
        // cart owner's uid so getImagesDB can read this wall's images on the new device.
        if (!uid && cart && cart.uid) { uid = cart.uid; LS("uniqueUserId", uid); }
        if (!uid) return null;
        return load(uid, sid);
      })
      .catch(() => (uid ? load(uid, sid) : null)); // lookup failed -> load if we have a uid
  }, [load]);

  return { wall, ensureSession, uploadImage, updateCrop, hydrateForCheckout, restore };
}
